import axios from '@axios'

/**
 * Debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Create debounced async validator for email uniqueness
 * @param {number|null} excludeUserId - User ID to exclude (for edit forms)
 * @returns {Function} Async validator function
 */
export const createAsyncEmailValidator = (excludeUserId = null) => {
  let pendingRequest = null

  const checkEmail = async (value) => {
    // Cancel previous request if still pending
    if (pendingRequest) {
      pendingRequest.cancel()
    }

    try {
      // Create cancelable request
      const { CancelToken } = axios
      const source = CancelToken.source()
      pendingRequest = source

      const params = { email: value }
      if (excludeUserId) {
        params.exclude_user_id = excludeUserId
      }

      const { data } = await axios.get('/api/auth/check-email/', {
        params,
        cancelToken: source.token,
      })

      pendingRequest = null
      return data.available ? true : data.message
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was canceled, return true to allow next validation
        return true
      }
      // On network error, allow form submission (backend will catch it)
      return true
    }
  }

  // Debounce the check function
  const debouncedCheck = debounce(async (value, resolve) => {
    const result = await checkEmail(value)
    resolve(result)
  }, 500) // 500ms debounce

  return async (value) => {
    if (!value || value.trim() === '') return true

    return new Promise((resolve) => {
      debouncedCheck(value, resolve)
    })
  }
}

/**
 * Create debounced async validator for phone uniqueness
 * @param {number|null} companyId - Company ID for scoped check
 * @param {number|null} excludeUserId - User ID to exclude (for edit forms)
 * @returns {Function} Async validator function
 */
export const createAsyncPhoneValidator = (companyId = null, excludeUserId = null) => {
  let pendingRequest = null

  const checkPhone = async (value) => {
    // Cancel previous request if still pending
    if (pendingRequest) {
      pendingRequest.cancel()
    }

    try {
      // Create cancelable request
      const { CancelToken } = axios
      const source = CancelToken.source()
      pendingRequest = source

      const params = { phone: value }
      if (companyId) {
        params.company_id = companyId
      }
      if (excludeUserId) {
        params.exclude_user_id = excludeUserId
      }

      const { data } = await axios.get('/api/auth/check-phone/', {
        params,
        cancelToken: source.token,
      })

      pendingRequest = null
      return data.available ? true : data.message
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was canceled, return true to allow next validation
        return true
      }
      // On network error, allow form submission (backend will catch it)
      return true
    }
  }

  // Debounce the check function
  const debouncedCheck = debounce(async (value, resolve) => {
    const result = await checkPhone(value)
    resolve(result)
  }, 500)

  return async (value) => {
    if (!value || value.trim() === '') return true

    return new Promise((resolve) => {
      debouncedCheck(value, resolve)
    })
  }
}

/**
 * Create debounced async validator for username uniqueness
 * @param {number|null} excludeUserId - User ID to exclude (for edit forms)
 * @returns {Function} Async validator function
 */
export const createAsyncUsernameValidator = (excludeUserId = null) => {
  let pendingRequest = null

  const checkUsername = async (value) => {
    // Cancel previous request if still pending
    if (pendingRequest) {
      pendingRequest.cancel()
    }

    try {
      // Create cancelable request
      const { CancelToken } = axios
      const source = CancelToken.source()
      pendingRequest = source

      const params = { username: value }
      if (excludeUserId) {
        params.exclude_user_id = excludeUserId
      }

      const { data } = await axios.get('/api/auth/check-username/', {
        params,
        cancelToken: source.token,
      })

      pendingRequest = null
      return data.available ? true : data.message
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was canceled, return true to allow next validation
        return true
      }
      // On network error, allow form submission (backend will catch it)
      return true
    }
  }

  // Debounce the check function
  const debouncedCheck = debounce(async (value, resolve) => {
    const result = await checkUsername(value)
    resolve(result)
  }, 500)

  return async (value) => {
    if (!value || value.trim() === '') return true

    return new Promise((resolve) => {
      debouncedCheck(value, resolve)
    })
  }
}
